// /src/store/themeStore.ts

import { create } from 'zustand';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'brickerator-theme';

function isTheme(v: unknown): v is Theme {
  return v === 'dark' || v === 'light';
}

function readDomTheme(): Theme | null {
  if (typeof document === 'undefined') return null;
  const v = document.documentElement?.dataset?.theme;
  return isTheme(v) ? v : null;
}

function readStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(v) ? v : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
  } catch {
    return 'dark';
  }
}

function applyThemeToDom(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  // Helps native form controls (inputs/selects/scrollbars) match the theme.
  (document.documentElement.style as any).colorScheme = theme;
}

type ThemeState = {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
};

export const useThemeStore = create<ThemeState>((set, get) => {
  const initial = readDomTheme() ?? readStoredTheme() ?? systemTheme();

  // Ensure DOM is always in sync (even if index.html didn't run the bootstrap script).
  applyThemeToDom(initial);

  return {
    theme: initial,

    setTheme: (t) => {
      const next: Theme = t === 'light' ? 'light' : 'dark';
      set({ theme: next });

      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // ignore
        }
      }

      applyThemeToDom(next);
    },

    toggleTheme: () => {
      const cur = get().theme;
      get().setTheme(cur === 'dark' ? 'light' : 'dark');
    },
  };
});
